import {
  KeyRoundIcon,
  Loader2Icon,
  LockIcon,
  LockOpenIcon,
  MailCheckIcon,
  MailIcon,
  MoreHorizontalIcon,
  PencilIcon,
  RotateCcwIcon,
  Trash2Icon,
  UserCheckIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  UserStatus,
  deleteUser,
  getLeadHandover,
  lockUser,
  resendEmailVerification,
  resendInvitation,
  unlockUser,
  type ManagedUser,
} from '@/api/users'
import { useApiErrorMessage } from '@/api/errors'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  LockOrDeleteDialog,
  type Confirmation,
  type LockOrDelete,
} from '@/components/users/LockOrDeleteDialog'
import { userLabel } from '@/lib/userDisplay'

const SPINNING = 'animate-spin motion-reduce:animate-[spin_2.4s_linear_infinite]'

/**
 * Everything that can be done to one row. Editing, setting a password and activating open dialogs
 * owned by the page — there is one of each, not one per row — while the rest is handled here.
 *
 * The two irreversible-feeling actions ask first — and ask who takes over the account's leads.
 * Unlocking, restoring and resending do not: they add access or send a mail, and both are
 * trivially repeatable.
 */
export function UserRowActions({
  user,
  isSelf,
  onEdit,
  onSetPassword,
  onActivate,
  onChanged,
}: {
  user: ManagedUser
  /** Locking or deleting yourself is refused by the backend; there is no point offering it. */
  isSelf: boolean
  onEdit: () => void
  onSetPassword: () => void
  /** Sets a first password on an invited account, for when the invitation never arrives. */
  onActivate: () => void
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const [confirming, setConfirming] = useState<Confirmation | null>(null)
  const [busy, setBusy] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [preparing, setPreparing] = useState<LockOrDelete | null>(null)
  const preparation = useRef<AbortController | null>(null)

  useEffect(() => () => preparation.current?.abort(), [])

  const name = userLabel(user, t('users.noName'))
  const deleted = user.status === UserStatus.DELETED

  async function run(action: () => Promise<unknown>, success: string, failure: string) {
    if (busy) {
      return
    }
    setBusy(true)
    try {
      await action()
      toast.success(success)
      onChanged()
    } catch (error) {
      // The interesting failures here are the guards — "this is the last administrator" — and
      // they are the whole reason the message is not a generic one.
      toast.error(errorMessage(error, failure))
    } finally {
      setBusy(false)
      setConfirming(null)
    }
  }

  /**
   * Counts the account's leads **before** the confirmation opens, with the menu held open and a
   * spinner on the item in the meantime. Opened first and filled in afterwards, the dialog grew
   * under the pointer the moment the numbers arrived; this way it opens once, at its final size.
   * A count that fails still opens it — locking somebody out can be urgent, and the dialog says
   * what happens without one.
   */
  function prepare(action: LockOrDelete) {
    preparation.current?.abort()
    const controller = new AbortController()
    preparation.current = controller
    setPreparing(action)
    getLeadHandover(user.id, controller.signal)
      .then(
        (preview) => preview,
        () => null,
      )
      .then((preview) => {
        if (controller.signal.aborted) {
          return
        }
        setPreparing(null)
        setMenuOpen(false)
        setConfirming({ action, preview })
      })
  }

  function onMenuOpenChange(open: boolean) {
    setMenuOpen(open)
    if (!open) {
      // Closed while still counting: nobody is waiting for that dialog any more.
      preparation.current?.abort()
      setPreparing(null)
    }
  }

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={onMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={t('users.actions.menu', { name })}>
            <MoreHorizontalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {deleted ? (
            <DropdownMenuItem
              onSelect={() =>
                void run(
                  () => unlockUser(user.id),
                  t('users.unlock.success', { name }),
                  t('users.unlock.failed'),
                )
              }
            >
              <RotateCcwIcon />
              {t('users.actions.restore')}
            </DropdownMenuItem>
          ) : (
            <>
              {user.status === UserStatus.INVITED && user.email && (
                <DropdownMenuItem
                  onSelect={() =>
                    void run(
                      () => resendInvitation(user.id),
                      t('users.resend.success', { email: user.email }),
                      t('users.resend.failed'),
                    )
                  }
                >
                  <MailIcon />
                  {t('users.actions.resend')}
                </DropdownMenuItem>
              )}
              {/* Right under the resend, as what to try once resending has not helped. */}
              {user.status === UserStatus.INVITED && (
                <DropdownMenuItem onSelect={onActivate}>
                  <UserCheckIcon />
                  {t('users.actions.activate')}
                </DropdownMenuItem>
              )}
              {/* Only past the invitation: an invited account's link already carries the
                  verification, so offering both would be two buttons for one job. */}
              {user.status !== UserStatus.INVITED && !user.emailVerified && user.email && (
                <DropdownMenuItem
                  onSelect={() =>
                    void run(
                      () => resendEmailVerification(user.id),
                      t('users.verify.success', { email: user.email }),
                      t('users.verify.failed'),
                    )
                  }
                >
                  <MailCheckIcon />
                  {t('users.actions.resendVerification')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={onEdit}>
                <PencilIcon />
                {t('users.actions.edit')}
              </DropdownMenuItem>
              {/* On an invited account this is what activating does, and two items for one
                  request would leave an administrator wondering which one is the right one. */}
              {user.status !== UserStatus.INVITED && (
                <DropdownMenuItem onSelect={onSetPassword}>
                  <KeyRoundIcon />
                  {t('users.actions.setPassword')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {user.status === UserStatus.LOCKED ? (
                <DropdownMenuItem
                  onSelect={() =>
                    void run(
                      () => unlockUser(user.id),
                      t('users.unlock.success', { name }),
                      t('users.unlock.failed'),
                    )
                  }
                >
                  <LockOpenIcon />
                  {t('users.actions.unlock')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled={isSelf || preparing !== null}
                  onSelect={(event) => {
                    event.preventDefault()
                    prepare('lock')
                  }}
                >
                  {preparing === 'lock' ? <Loader2Icon className={SPINNING} /> : <LockIcon />}
                  {t('users.actions.lock')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                variant="destructive"
                disabled={isSelf || preparing !== null}
                onSelect={(event) => {
                  event.preventDefault()
                  prepare('delete')
                }}
              >
                {preparing === 'delete' ? <Loader2Icon className={SPINNING} /> : <Trash2Icon />}
                {t('users.actions.delete')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <LockOrDeleteDialog
        user={user}
        name={name}
        confirmation={confirming}
        busy={busy}
        onCancel={() => setConfirming(null)}
        onConfirm={(handover) => {
          if (confirming?.action === 'delete') {
            void run(
              () => deleteUser(user.id, handover),
              t('users.delete.success', { name }),
              t('users.delete.failed'),
            )
          } else {
            void run(
              () => lockUser(user.id, handover),
              t('users.lock.success', { name }),
              t('users.lock.failed'),
            )
          }
        }}
      />
    </>
  )
}
